import { MessageBarIntent } from "@fluentui/react-components";
import { CSSProperties, useContext } from "react";
import { MessageContext } from ".";

export interface MessageAction {
	name: string;
	onActionClick: () => void;
}

export interface Message {
	key?: string;
	text: string;
	duration?: number;
	intent: MessageBarIntent;
	action?: MessageAction;
	isPermanent?: boolean;
	bodyStyle?: React.CSSProperties; 
}

export interface IdentifiableMessage extends Message {
	id: string;
}

export interface MessageOutletProps {
	namespace: Namespace;
	style?: CSSProperties;
}

export interface MessageProviderProps {
	children: JSX.Element;
}

export interface MessageContextType {
	messages: Map<string, IdentifiableMessage[]>;
	show: (namespace: Namespace, message: Message) => void;
	hide: (namespace: Namespace, id: string) => void;
}

export const useMessage = (): MessageContextType => {
	const context = useContext(MessageContext);
	if (!context) {
		throw new Error("useMessage must be used within a MessageProvider");
	}

	return context;
};

export const debounce = <F extends (...args: any[]) => any>(
	func: F,
	wait: number
): ((...args: Parameters<F>) => void) => {
	let timeout: ReturnType<typeof setTimeout>;

	return function executedFunction(...args: Parameters<F>) {
		const later = () => {
			clearTimeout(timeout);
			func(...args);
		};

		clearTimeout(timeout);
		timeout = setTimeout(later, wait);
	};
};

export type Namespace = "global" | "workspace" | "dialog" | "historyWorkspace" | "allInteractions";

export const DefaultMessageDuration = 8000;
